import type { RecurringAllowance } from "../api/types";
import { formatCents, formatDateTime } from "../lib/money";

const CADENCE_LABEL: Record<string, string> = {
  daily: "every day",
  weekly: "every week",
  biweekly: "every 2 weeks",
  monthly: "every month",
};

/**
 * A parent's list of a child's allowance rules, with the controls to pause,
 * edit, or stop each one.
 */
export function AllowanceList({
  allowances,
  savePercent,
  onEdit,
  onToggle,
  onDelete,
}: {
  allowances: RecurringAllowance[];
  /** The child's chosen split, shown here because the parent cannot set it. */
  savePercent: number;
  onEdit: (allowance: RecurringAllowance) => void;
  onToggle: (allowance: RecurringAllowance) => void;
  onDelete: (allowance: RecurringAllowance) => void;
}) {
  if (allowances.length === 0) {
    return (
      <p className="muted" style={{ padding: "var(--gap) var(--gap-lg)" }}>
        No recurring payments yet.
      </p>
    );
  }

  return (
    <div>
      {allowances.map((a) => (
        <div
          key={a.id}
          className={`allowance-row ${a.active ? "" : "allowance-row--paused"}`}
        >
          <div className="allowance-row__main">
            <span className="allowance-row__amount">
              {formatCents(a.amountCents)} {CADENCE_LABEL[a.cadence] ?? a.cadence}{" "}
              {a.active ? null : <span className="pill">Paused</span>}
            </span>
            <span className="allowance-row__meta">
              {`${100 - savePercent}% Spend · ${savePercent}% Save (their choice)`}
              {a.active ? ` · next on ${formatDateTime(a.nextRunAt)}` : ""}
            </span>
          </div>

          <div className="allowance-row__actions">
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => onToggle(a)}
            >
              {a.active ? "Pause" : "Resume"}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => onEdit(a)}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small btn--danger"
              onClick={() => onDelete(a)}
            >
              Stop
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
